import { loadDeviceIntegrations } from "./attendanceDeviceIntegration";

export const LOCATION_REQUIRED_MESSAGE =
  "Turn on location on this device, allow access in the browser, then punch again.";

export function isValidPunchCoords(lat, lng) {
  const a = Number(lat);
  const b = Number(lng);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
  if (Math.abs(a) > 90 || Math.abs(b) > 180) return false;
  if (Math.abs(a) < 0.000001 && Math.abs(b) < 0.000001) return false;
  return true;
}

export function isOnlinePunchSource(source = "web") {
  const s = String(source || "web").toLowerCase();
  return s === "web" || s === "mobile";
}

/** True when attendance policy expects GPS on web/mobile punches. */
export function policyTracksPunchLocation(policy) {
  if (!policy) return false;
  return !!(
    policy.trackLocation ||
    policy.restrictLocationForOnlineCheckIn ||
    policy.enableGeoFencing
  );
}

export function policyRequiresPunchGps(policy, source = "web") {
  if (!policyTracksPunchLocation(policy)) return false;
  return isOnlinePunchSource(source);
}

export async function reverseGeocodeAddress(lat, lng) {
  if (!isValidPunchCoords(lat, lng)) return "";
  try {
    const ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
    const timer = ctrl ? setTimeout(() => ctrl.abort(), 4000) : null;
    const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${encodeURIComponent(
      lat
    )}&lon=${encodeURIComponent(lng)}&zoom=18&addressdetails=0`;
    const res = await fetch(url, {
      headers: { Accept: "application/json" },
      signal: ctrl?.signal,
    });
    if (timer) clearTimeout(timer);
    if (!res.ok) return "";
    const data = await res.json();
    return String(data?.display_name || "").trim();
  } catch {
    return "";
  }
}

export async function capturePunchLocation() {
  const coords = await new Promise((resolve, reject) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      const err = new Error(LOCATION_REQUIRED_MESSAGE);
      err.code = "LOCATION_UNAVAILABLE";
      reject(err);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        resolve({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy:
            typeof pos.coords.accuracy === "number" ? pos.coords.accuracy : null,
        });
      },
      () => {
        const err = new Error(LOCATION_REQUIRED_MESSAGE);
        err.code = "LOCATION_DENIED";
        reject(err);
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
    );
  });
  if (!isValidPunchCoords(coords.latitude, coords.longitude)) {
    const err = new Error(LOCATION_REQUIRED_MESSAGE);
    err.code = "LOCATION_UNAVAILABLE";
    throw err;
  }
  // Coords are required for punch; address is best-effort (server also geocodes).
  let locationAddress = "";
  try {
    locationAddress = await reverseGeocodeAddress(coords.latitude, coords.longitude);
  } catch {
    /* ignore — backend enrichPunchLocation may fill address */
  }
  return { ...coords, locationAddress: locationAddress || "" };
}

export async function resolvePunchLocationPayload(policy, source = "web") {
  if (!policyRequiresPunchGps(policy, source)) return {};
  return capturePunchLocation();
}

export function mapsUrlForCoords(lat, lng) {
  if (!isValidPunchCoords(lat, lng)) return "";
  return `https://www.google.com/maps?q=${encodeURIComponent(`${Number(lat)},${Number(lng)}`)}`;
}

export function describeBiometricDevice(deviceId) {
  const id = String(deviceId || "").trim();
  if (!id) return null;
  const { devices } = loadDeviceIntegrations();
  const match = (devices || []).find(
    (d) =>
      String(d.id || "") === id ||
      String(d.deviceSerial || "").trim() === id
  );
  if (!match) {
    return { label: id, detail: "Device ID" };
  }
  const parts = [match.name, match.deviceSerial, match.location].filter(Boolean);
  return {
    label: match.name || match.deviceSerial || id,
    detail: parts.filter((p, i) => i === 0 || p !== parts[0]).join(" · ") || id,
  };
}
